
import { useState } from 'react';

interface Expense {
  id: string;
  name: string;
  amount: number;
  category: string;
  date: string;
  description?: string;
  createdAt: string;
}

interface ExpenseListProps {
  expenses: Expense[];
  onEdit: (expense: Expense) => void;
  onDelete: (id: string) => void;
  onDeleteAll: () => void;
  loading: boolean;
}

export function ExpenseList({ expenses, onEdit, onDelete, onDeleteAll, loading }: ExpenseListProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) {
      return (amount / 1000000000).toFixed(1) + ' tỷ ₫';
    } else if (amount >= 1000000) {
      return (amount / 1000000).toFixed(0) + ' triệu ₫';
    }
    return amount.toLocaleString('vi-VN') + ' ₫';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN');
  };

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      'marketing': 'ri-megaphone-line',
      'office': 'ri-building-line',
      'travel': 'ri-car-line',
      'equipment': 'ri-tools-line',
      'utilities': 'ri-lightbulb-line',
      'other': 'ri-more-line'
    };
    return icons[category] || 'ri-money-dollar-circle-line';
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'marketing': 'bg-purple-100 text-purple-800',
      'office': 'bg-blue-100 text-blue-800',
      'travel': 'bg-green-100 text-green-800',
      'equipment': 'bg-orange-100 text-orange-800',
      'utilities': 'bg-yellow-100 text-yellow-800',
      'other': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const handleDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deletingId) {
      onDelete(deletingId);
      setDeletingId(null);
    }
    setShowDeleteConfirm(false);
  };

  const confirmDeleteAll = () => {
    onDeleteAll();
    setShowDeleteAllConfirm(false);
  };

  if (expenses.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
        <div className="text-center">
          <i className="ri-file-list-line text-4xl sm:text-5xl text-gray-400 mb-3 sm:mb-4"></i>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Chưa có khoản chi nào</h3>
          <p className="text-sm sm:text-base text-gray-500">
            Bắt đầu thêm các khoản chi để theo dõi tài chính của bạn
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Danh sách chi phí</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              {expenses.length} khoản chi • Tổng: {formatCurrency(expenses.reduce((sum, expense) => sum + expense.amount, 0))}
            </p>
          </div>
          {expenses.length > 0 && (
            <button
              onClick={() => setShowDeleteAllConfirm(true)}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-2 min-h-[44px]"
            >
              <i className="ri-delete-bin-line"></i>
              <span>Xóa tất cả</span>
            </button>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tên khoản chi
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Danh mục
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Số tiền
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {expenses.map((expense) => (
              <tr key={expense.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{expense.name}</div>
                    {expense.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">{expense.description}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(expense.category)}`}>
                    <i className={`${getCategoryIcon(expense.category)} mr-1`}></i>
                    {expense.category}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                  {formatCurrency(expense.amount)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {formatDate(expense.date)}
                </td>
                <td className="px-6 py-4 text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => onEdit(expense)}
                      className="text-blue-600 hover:text-blue-900 p-1"
                      title="Chỉnh sửa"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(expense.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                      title="Xóa"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden divide-y divide-gray-200">
        {expenses.map((expense) => (
          <div key={expense.id} className="p-4 space-y-3">
            {/* Header */}
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 truncate">{expense.name}</h3>
                <p className="text-lg font-bold text-gray-900 mt-1">{formatCurrency(expense.amount)}</p>
              </div>
              <div className="flex space-x-2 ml-3">
                <button
                  onClick={() => onEdit(expense)}
                  className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
                  title="Chỉnh sửa"
                >
                  <i className="ri-edit-line text-sm"></i>
                </button>
                <button
                  onClick={() => handleDelete(expense.id)}
                  className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                  title="Xóa"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>

            {/* Details */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(expense.category)}`}>
                  <i className={`${getCategoryIcon(expense.category)} mr-1`}></i>
                  {expense.category}
                </span>
                <span className="text-xs text-gray-500">{formatDate(expense.date)}</span>
              </div>
              
              {expense.description && (
                <p className="text-sm text-gray-600 line-clamp-2">{expense.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <div className="text-center">
              <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Xác nhận xóa</h3>
              <p className="text-sm text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa khoản chi này? Hành động này không thể hoàn tác.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <div className="text-center">
              <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Xóa tất cả chi phí</h3>
              <p className="text-sm text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa tất cả {expenses.length} khoản chi? Hành động này không thể hoàn tác.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteAllConfirm(false)}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmDeleteAll}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  Xóa tất cả
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="w-6 h-6 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
}
